import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Share } from '@phosphor-icons/react';

// Install PWA prompt component
export const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed or dismissed
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    if (dismissed || isStandalone) return;

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    if (iOS) {
      // Show iOS install instructions after 3 seconds
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // For Android/Desktop - listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-24 left-4 right-4 z-[9998] md:left-auto md:right-4 md:w-80"
      >
        <div className="bg-gradient-to-br from-[#1F2937] to-[#18261E] border border-[#3E5248] rounded-2xl p-5 shadow-2xl">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-[#889990] hover:text-white"
          >
            <X size={20} />
          </button>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#344840] flex items-center justify-center flex-shrink-0">
              <span className="brand-logo text-xl text-gray-100">h</span>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-medium mb-1">Install habitué</h3>
              <p className="text-sm text-[#889990] mb-4">
                {isIOS 
                  ? "Add to Home Screen for the best experience"
                  : "Install our app for quick access"
                }
              </p>

              {isIOS ? (
                <div className="text-xs text-[#889990] space-y-2">
                  <div className="flex items-center gap-2">
                    <Share size={16} />
                    <span>Tap the Share button</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Plus size={16} />
                    <span>Select "Add to Home Screen"</span>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleInstall}
                  className="bg-[#C3A365] text-[#18261E] hover:bg-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
                >
                  Install Now
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Splash screen component
export const SplashScreen = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      className="fixed inset-0 z-[10000] bg-gradient-to-br from-[#18261E] via-[#283D32] to-[#18261E] flex items-center justify-center"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        >
          <div className="brand-logo text-6xl text-gray-100 mb-4">habitué</div>
        </motion.div>
        
        <motion.p
          className="text-[#889990] text-sm tracking-widest uppercase"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          Your Local Network
        </motion.p>

        <motion.div
          className="mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className="w-8 h-8 mx-auto border-2 border-green-600 border-t-gray-300 rounded-full animate-spin" />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default { InstallPrompt, SplashScreen };
